import { Checkbox, Combobox, Group, Input, ThemeIcon, Tooltip, useCombobox } from "@mantine/core";
import { useRef, useState } from "react";

import classes from "./Dropdown.module.css";
import { DropdownBaseTarget, DropdownChevron, DropdownSearchTarget } from "./components";
import {
  getPropsFromComboboxItem,
  getReactNodeFromItems,
  isItemInQuery,
  useDropdownPosition,
} from "./utils";

import { IconCheck, IconInfo } from "@/components/icons";

import type { GroupProps, MantineSize, MantineStyleProps, SelectProps } from "@mantine/core";
import type { FC } from "react";
import type { DropdownChevronProps } from "./components";
import type { DropdownItem } from "./types";

/**
 * Dropdown component for selecting a value from a list of options.
 *
 * Supports single selection, optional search, clearable values, and custom rendering for options.
 * Integrates with Mantine's Combobox and Input components for accessibility and consistent UI.
 *
 * @component
 * @example
 * <Dropdown
 *   label="Choose an option"
 *   data={["Option 1", "Option 2", { label: "Option 3", value: "3" }]}
 *   value={selected}
 *   onChange={setSelected}
 *   searchable
 *   clearable
 *   placeholder="Select..."
 * />
 *
 * @typedef {Object} DropdownProps
 * @property {Array<string | DropdownItem>} data - List of options to display in the dropdown.
 * @property {boolean} [allowDeselect=true] - Allow deselecting the current value by clicking again.
 * @property {boolean} [clearable=false] - Show a clear button to remove the selection.
 * @property {string} [defaultValue] - The initial value of the dropdown.
 * @property {string} [description] - Description text displayed above the dropdown.
 * @property {string} [emptyMessage] - Message to display when no options are available.
 * @property {string} [error] - Error message to display below the dropdown.
 * @property {string} [iconVariant] - Variant for the dropdown chevron icon.
 * @property {Object} [inputProps] - Additional props for the input element.
 * @property {string} [label] - Label text displayed above the dropdown.
 * @property {(value?: string | null) => void} [onChange] - Callback fired when the value changes.
 * @property {Object} [optionProps] - Additional props for each option group.
 * @property {string} [placeholder] - Placeholder text for the dropdown input.
 * @property {boolean} [searchable=false] - Enable search input for filtering options.
 * @property {string} [size="md"] - Size of the dropdown (e.g., 'sm', 'md', 'lg').
 * @property {string} [value] - Controlled value for the dropdown.
 * @property {"default" | "checkbox" | "check"} [variant="default"] - Option rendering variant.
 *
 * @returns {JSX.Element} Dropdown component.
 */
export type DropdownProps = Pick<
  SelectProps,
  | "allowDeselect"
  | "clearable"
  | "defaultValue"
  | "description"
  | "error"
  | "label"
  | "placeholder"
  | "searchable"
  | "value"
> & {
  data: (string | DropdownItem)[];
  disabled?: boolean;
  emptyMessage?: string;
  flex?: MantineStyleProps["flex"];
  iconVariant?: DropdownChevronProps["variant"];
  inputProps?: MantineStyleProps;
  onChange?: (value?: string | null) => void;
  optionProps?: GroupProps;
  size?: MantineSize;
  variant?: "default" | "checkbox" | "check";
  required?: boolean;
  withAsterisk?: boolean;
  showLessElements?: boolean;
  tooltip?: string | React.ReactNode;
};

type DropdownComponent = FC<DropdownProps> & {
  BaseTarget: typeof DropdownBaseTarget;
  Chevron: typeof DropdownChevron;
  SearchTarget: typeof DropdownSearchTarget;
};

export const Dropdown: DropdownComponent = ({
  clearable = false,
  data = [],
  defaultValue,
  description,
  disabled = false,
  emptyMessage,
  error,
  flex,
  iconVariant,
  inputProps,
  label,
  onChange,
  optionProps,
  placeholder,
  searchable = false,
  size = "md",
  value,
  variant = "default",
  required = false,
  allowDeselect = !required,
  withAsterisk = false,
  showLessElements = false,
  tooltip,
}) => {
  const ref = useRef<HTMLDivElement>(null);
  const [currentValue, setCurrentValue] = useState(defaultValue);
  const [query, setQuery] = useState("");
  const [opened, setOpened] = useState(false);
  const position = useDropdownPosition(ref);
  const combobox = useCombobox({
    onDropdownOpen: () => {
      setOpened(true);
      combobox.focusTarget();
    },
    onDropdownClose: () => {
      setOpened(false);
      combobox.resetSelectedOption();
      setQuery("");
    },
    opened,
  });

  const options = searchable && !!query ? data.filter(isItemInQuery(query)) : data;
  const getReactNode = getReactNodeFromItems(data);

  function handleOptionSubmit(submitValue: string) {
    if (onChange) {
      onChange(submitValue === value && allowDeselect ? undefined : submitValue);
    } else {
      setCurrentValue(submitValue === currentValue && allowDeselect ? undefined : submitValue);
    }
    combobox.closeDropdown();
  }

  function handleQueryBlur() {
    combobox.closeDropdown();
    setQuery("");
  }

  function handleQueryChange(value: string) {
    combobox.openDropdown();
    setQuery(value);
  }

  function handleValueClear() {
    if (onChange) {
      onChange(undefined);
    } else {
      setCurrentValue(undefined);
    }
    setQuery("");
  }

  function isActive(option?: string | null) {
    if (option) {
      return option === value || option === currentValue;
    }

    return false;
  }

  const maxHeightStyle: React.CSSProperties = showLessElements
    ? { maxHeight: 4 * 36, overflowY: "auto" as const }
    : {};

  return (
    <Input.Wrapper flex={flex} withAsterisk={withAsterisk}>
      {label && (
        <Input.Label required={withAsterisk} size={size} className={classes.label}>
          {tooltip ? (
            <span style={{ display: "inline-flex", alignItems: "center", gap: 2 }}>
              {label}
              <Tooltip
                label={tooltip}
                withArrow
                events={{ hover: true, focus: true, touch: true }}
                position="top"
                offset={4}
                maw={300}
                openDelay={100}
                multiline
              >
                <IconInfo size={18} />
              </Tooltip>
            </span>
          ) : (
            label
          )}
        </Input.Label>
      )}

      {description && <Input.Description size={size}>{description}</Input.Description>}
      <Combobox
        disabled={disabled}
        offset={0}
        onOptionSubmit={handleOptionSubmit}
        store={combobox}
        withinPortal={true}
        zIndex={400}
      >
        <Combobox.Target>
          {searchable ? (
            <DropdownSearchTarget
              clearable={clearable}
              disabled={disabled}
              iconVariant={iconVariant}
              onBlur={handleQueryBlur}
              onChange={handleQueryChange}
              onClear={handleValueClear}
              onClick={combobox.openDropdown}
              onFocus={combobox.openDropdown}
              opened={opened}
              placeholder={placeholder}
              position={position}
              query={query}
              size={size}
              value={value || currentValue}
              {...inputProps}
            >
              {getReactNode(value || currentValue)}
            </DropdownSearchTarget>
          ) : (
            <DropdownBaseTarget
              clearable={clearable}
              disabled={disabled}
              iconVariant={iconVariant}
              onClear={handleValueClear}
              onClick={combobox.toggleDropdown}
              opened={opened}
              placeholder={placeholder}
              position={position}
              size={size}
              value={value || currentValue}
              {...inputProps}
            >
              {getReactNode(value || currentValue)}
            </DropdownBaseTarget>
          )}
        </Combobox.Target>
        <Combobox.Dropdown ref={ref} classNames={{ dropdown: classes.dropdown }}>
          <Combobox.Options style={maxHeightStyle}>
            {options.map((item, idx) => {
              const { children, disabled, label, value } = getPropsFromComboboxItem(item);
              const active = isActive(value);

              return (
                <Combobox.Option key={idx} active={active} value={value} disabled={disabled}>
                  <Group gap="sm" wrap="nowrap" {...optionProps}>
                    {variant === "checkbox" && (
                      <Checkbox checked={active} size="xs" variant="filled" />
                    )}
                    {children || label}
                    {variant === "check" && active && (
                      <ThemeIcon color="secondary" variant="transparent" size="xs">
                        <IconCheck size={16} />
                      </ThemeIcon>
                    )}
                  </Group>
                </Combobox.Option>
              );
            })}
            {options.length === 0 && <Combobox.Empty>{emptyMessage}</Combobox.Empty>}
          </Combobox.Options>
        </Combobox.Dropdown>
      </Combobox>
      {error && <Input.Error>{error}</Input.Error>}
    </Input.Wrapper>
  );
};

Dropdown.BaseTarget = DropdownBaseTarget;
Dropdown.Chevron = DropdownChevron;
Dropdown.SearchTarget = DropdownSearchTarget;
